// GuestActionPopup.js

import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import CloseIcon from '@mui/icons-material/Close';
import { cloneGuest } from '../accounts/cloneGuest';
import i18n from 'i18next';
import { Button, IconButton } from '@mui/material'; // Import MUI Button
/**
 * Popup component for guest user actions
 */
const GuestActionPopup = ({
  onClose,
  onContinueWithTemp,
  theme,
  currentTheme,
  updateSession,
  isPremiumContent = false
}) => {
  const { t } = useTranslation();

  console.log('[GuestActionPopup] Rendering popup. isPremiumContent:', isPremiumContent, 'currentTheme:', currentTheme);

  const [isCreating, setIsCreating] = useState(false);

  // Determine if we're on ooo2 theme (only has free content)
  const isOoo2Theme = currentTheme === 'ooo2' || window.location.hostname.includes('ooo2');

  // Generate appropriate sign up link based on theme
  const getSignUpLink = () => {
    const language = i18n.language;
    const langParam = language !== 'en' ? `?lang=${language}` : '';

    if (currentTheme === "wallmuse") {
      return 'https://wallmuse.com/sign-up/';
    } else if (currentTheme === "ooo2") {
      // Special case for ooo2 theme
      // Map language codes to their respective URL paths
      const langPathMap = {
        'en': 'product/free-accounts/',
        'fr': 'produit/compte-gratuit/',
        'es': 'producto/cuenta-gratuita/',
        'de': 'produkt/kostenloses-konto/',
        'it': 'prodotto/account-gratuito/',
        'pt': 'produto/contas-gratuitas/',
        'nl': 'product/gratis-account/',
        'no': 'produkt/gratis-konto/',
        'uk': 'product/безкоштовний-обліковий-запис/',
        'pl': 'produkt/darmowe-konto/',
        'hr': 'proizvod/besplatni-racun/'
      };

      // Get the appropriate path for the current language
      const path = langPathMap[language] || langPathMap['en']; // Default to English path

      return `https://ooo2.wallmuse.com/${path}${langParam}`;
    } else {
      return `https://${currentTheme}.wallmuse.com/sign-up/`;
    }
  };

  // Handle temporary account creation
  const handleGuestAccount = async () => {
    try {
      setIsCreating(true); // ✅ KEEP: Shows loading state on button
      console.log('[GuestActionPopup] Creating temporary account');

      // ⚠️ MODIFY: This part might be causing issues
      // Instead of setting localStorage directly, just dispatch the event
      // localStorage.setItem('currentSetupPhase', 'preparing'); // Remove this line
      window.dispatchEvent(new CustomEvent('account-phase-change', {
        detail: { phase: 'preparing' }
      }));

      const result = await cloneGuest(updateSession); // ✅ KEEP: Core functionality
      console.log('[GuestActionPopup] Temporary account created:', result);

      // ✅ KEEP: Core callback functionality
      if (onContinueWithTemp && typeof onContinueWithTemp === 'function') {
        console.log('[GuestActionPopup] Calling onContinueWithTemp with result');
        onContinueWithTemp(result);
      } else {
        console.error('[GuestActionPopup] onContinueWithTemp is not a function');
      }

      // ✅ KEEP: Closing the popup
      if (onClose && typeof onClose === 'function') {
        console.log('[GuestActionPopup] Calling onClose');
        onClose();
      } else {
        console.error('[GuestActionPopup] onClose is not a function');
      }
    } catch (error) {
      console.error('[GuestActionPopup] Error creating temporary account:', error);
      // ⚠️ MODIFY: This might be related to the issue
      // localStorage.removeItem('currentSetupPhase'); // Remove this line
      alert(t('temp_account_error') || 'Error creating temporary account. Please try again.');
    } finally {
      setIsCreating(false); // ✅ KEEP: Resets loading state
    }
  };

  // Handle sign up
  const handleSignUp = () => {
    const signUpLink = getSignUpLink();
    console.log('[GuestActionPopup] Redirecting to sign up page:', signUpLink);
    window.location.href = signUpLink;
  };

  // Determine if we should show premium content message
  const showPremiumMessage = isPremiumContent && !isOoo2Theme;
  console.log('[GuestActionPopup] showPremiumMessage:', showPremiumMessage);

  // Close button handler - ensure it's a proper function
  const handleClose = () => {
    console.log('[GuestActionPopup] Close button clicked');
    if (onClose && typeof onClose === 'function') {
      onClose();
    } else {
      console.error('[GuestActionPopup] onClose is not a function or not provided');
    }
  };
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        position: 'relative',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '400px',
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
      }}>
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: theme.palette.text.secondary,
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CloseIcon />
        </button>

        <h3 style={{
          margin: '0 0 15px 0',
          color: theme.palette.primary.main,
          paddingRight: '20px' // Add space for close button
        }}>
          {showPremiumMessage
            ? (t('premium_content_title') || 'Premium Content')
            : (t('account_creation_title') || 'Account Creation')}
        </h3>

        {/* Premium content message */}
        {showPremiumMessage && (
          <p style={{ marginBottom: '20px' }}>
            {t('premium_content_message') || 'This content requires a premium subscription. Subscribe now to unlock all features.'}
          </p>
        )}

        {/* Standard message for free content */}
        {!showPremiumMessage && (
          <p style={{ marginBottom: '20px' }}>
            {t('account_options_message') || 'You need an account to save this action. Choose an option:'}
          </p>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          gap: '20px'
        }}>
          {/* Only show temp account option for free content */}
          {!showPremiumMessage && (
            <Button
              color="primary"
              onClick={handleGuestAccount}
              variant="contained"
              color="secondary"
              size="small"
              disabled={isCreating}
              sx={{ minWidth: '150px' }}
            >
              {isCreating ?
                (t('creating_account') || 'Creating...') :
                (t('no_sign_in') || 'No Sign In')}
            </Button>
          )}

          <Button
            onClick={handleSignUp}
            variant="contained"
            color="primary"
            size="small"
            disabled={isCreating}
            sx={{ minWidth: '150px' }}
          >
            {showPremiumMessage ?
              (t('subscribe_now') || 'Subscribe Now') :
              (t('with_sign_in') || 'With Sign In')}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div 
      className="guest-action-popup"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '20px',
        gap: '10px'
      }}>
        {/* Only show temp account option for free content */}
        {!showPremiumMessage && (
          <IconButton 
            onClick={handleGuestAccount}
            variant="contained" // Change to contained for consistent visual weight
            color="secondary" // Use secondary color to differentiate but keep styled
            disabled={isCreating}
            sx={{
              flex: 1,
              padding: '10px',
              fontSize: '14px',
              fontWeight: 'bold' // Match the weight of the other button
            }}
          >
            {isCreating ?
              (t('creating_account') || 'Creating...') :
              (t('no_sign_in') || 'No Sign In')}
          </IconButton>
        )}

        <IconButton
          onClick={handleSignUp}
          variant="contained"
          color="primary"
          disabled={isCreating} // Disable during creation process
          sx={{
            flex: 1,
            padding: '10px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          {showPremiumMessage ?
            (t('subscribe_now') || 'Subscribe Now') :
            (t('with_sign_in') || 'With Sign In')}
        </IconButton>
      </div>
    </div>
  );
};

export default GuestActionPopup;